'use client';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export type Language = 'en' | 'ru' | 'uz';

export const languages: Record<Language, string> = {
  en: 'English',
  ru: 'Русский',
  uz: "O'zbekcha",
};

const LANG_COOKIE = 'shot_lang';
const LANG_COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year

// Языки, для которых используется русский перевод
const russianLanguageCodes = ['uk', 'ru', 'kk'];

// Функция для определения языка по коду из Telegram
function getLanguageFromTelegramCode(languageCode?: string): Language {
  if (!languageCode) return 'en';

  if (languageCode === 'uz') {
    return 'uz';
  }

  if (russianLanguageCodes.includes(languageCode)) {
    return 'ru';
  }

  return 'en';
}

function getLanguageFromInitData(): Language | null {
  if (typeof window === 'undefined') return null;
  try {
    const urlParams = new URLSearchParams(window.location.hash.slice(1));
    const initData = urlParams.get('tgWebAppData');

    if (initData) {
      const params = new URLSearchParams(initData);
      const userParam = params.get('user');

      if (userParam) {
        const user = JSON.parse(decodeURIComponent(userParam));
        return getLanguageFromTelegramCode(user.language_code);
      }
    }
  } catch (error) {
    console.error('Failed to parse initData for language:', error);
  }

  return null;
}

function getLanguageFromBrowser(): Language | null {
  if (typeof navigator === 'undefined') return null;
  try {
    const langs = navigator.languages ?? [navigator.language];
    for (const lang of langs) {
      if (!lang) continue;
      const code = lang.toLowerCase().split('-')[0];
      if (code === 'uz') return 'uz';
      if (russianLanguageCodes.includes(code)) return 'ru';
      if (code === 'en') return 'en';
    }
  } catch {
    // SSR or restricted environment
  }
  return null;
}

function getLanguageFromCookie(): Language | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${LANG_COOKIE}=(ru|en|uz)(?:;|$)`),
  );
  return match ? (match[1] as Language) : null;
}

function writeLanguageCookie(language: Language): void {
  if (typeof document === 'undefined') return;
  // Secure flag conditional on https — required for prod, dropped in
  // local http://localhost dev so Chrome doesn't reject the cookie.
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? '; Secure'
    : '';
  document.cookie = `${LANG_COOKIE}=${language}; path=/; max-age=${LANG_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

interface LanguageState {
  language: Language;
  setLanguage: (language: Language) => void;
}

// Initial language is always 'ru' on SSR and first hydration pass to avoid
// React hydration mismatches. `detectAndApplyBrowserLanguage()` flips it to
// the real one in useEffect.
//
// `setLanguage` mirrors the new value into the `shot_lang` cookie so that
// `apps/landing-app/src/app/layout.tsx` (Server Component) reads the same
// language on the next request — keeping `<html lang>` and Zustand state
// in sync across navigations.
export const useLanguageStore = create<LanguageState>()(
  devtools(
    (set) => ({
      language: 'ru',
      setLanguage: (language) => {
        writeLanguageCookie(language);
        set({ language }, false, 'setLanguage');
      },
    }),
    { name: 'LanguageStore' }
  )
);

/** Pick best-match language from cookie (highest priority — user's saved
 * choice) → Telegram initData → browser, and write it to the store. Call
 * from a client-only useEffect after first render. */
export function detectAndApplyBrowserLanguage(): void {
  const detected =
    getLanguageFromCookie() ??
    getLanguageFromInitData() ??
    getLanguageFromBrowser();
  if (detected) {
    useLanguageStore.getState().setLanguage(detected);
  }
}

// Селекторы
export const useLanguage = () => useLanguageStore((state) => state.language);
export const useSetLanguage = () => useLanguageStore((state) => state.setLanguage);
